import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, unref as _unref, createBlock as _createBlock, renderList as _renderList, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["draggable", "aria-busy", "data-testid"]
const _hoisted_2 = { class: "flex items-start justify-between gap-2" }
const _hoisted_3 = { class: "min-w-0" }
const _hoisted_4 = { class: "text-[13px] font-bold text-textPrimary truncate" }
const _hoisted_5 = { class: "text-[11px] text-[#52526f] truncate" }
const _hoisted_6 = ["title", "data-testid"]
const _hoisted_7 = { class: "flex flex-col gap-0.5" }
const _hoisted_8 = {
  viewBox: "0 0 24 24",
  class: "w-3 h-3 shrink-0 text-[#7f7f8e]",
  "aria-hidden": "true"
}
const _hoisted_9 = ["d"]
const _hoisted_10 = { class: "truncate" }
const _hoisted_11 = {
  key: 0,
  class: "flex flex-col gap-1"
}
const _hoisted_12 = { class: "flex items-center justify-between gap-2" }
const _hoisted_13 = { class: "text-[11px] text-textPrimary truncate" }
const _hoisted_14 = {
  key: 0,
  class: "text-[10px] text-negative leading-3"
}
const _hoisted_15 = { class: "grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 rounded bg-[#f7f7fa] px-2 py-1.5 text-[10.5px]" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("dt", { class: "text-[#7f7f8e]" }, "Médico completar doc.", -1)
const _hoisted_17 = { class: "text-right font-semibold text-textPrimary tabular-nums" }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("dt", { class: "text-[#7f7f8e]" }, "Envio → decisão", -1)
const _hoisted_19 = { class: "text-right font-semibold text-textPrimary tabular-nums" }
const _hoisted_20 = /*#__PURE__*/_createElementVNode("dt", { class: "text-[#7f7f8e]" }, "Espera pelo OK", -1)
const _hoisted_21 = ["disabled", "data-testid"]

import { computed } from 'vue'
import { mdiCalendarClock, mdiCheckCircle } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { DOCUMENT_STATUS_LABEL, pendingReasonText } from '@/components/ProfessionalDocuments/labels'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import { PreAllocationColumn } from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationCard } from '@/types/qualification-eligibility.types'

import { countdown, formatStationStart, preAllocationTimeLabels, stationsByStart } from '../model'
import type { CountdownTone } from '../model'


export default /*#__PURE__*/_defineComponent({
  __name: 'PreAllocationCard',
  props: {
    card: {},
    isMoving: { type: Boolean, default: false },
    canMove: { type: Boolean, default: true }
  },
  emits: ["tryResolve", "dragStart"],
  setup(__props: any, { emit }) {

const props = __props;




const TIMER_CLASS: Record<CountdownTone, string> = {
  crit: 'bg-red-50 text-red-700',
  warn: 'bg-orange-50 text-orange-700',
  neutral: 'bg-[#f0f0f4] text-[#52526f]'
}

const resolved = computed(() => props.card.column === PreAllocationColumn.RESOLVED)
const canDrag = computed(() => props.canMove && !resolved.value && !props.isMoving)
const timer = computed(() => countdown(props.card))
const critical = computed(() => timer.value?.tone === 'crit')
const stations = computed(() => stationsByStart(props.card))
const times = computed(() => preAllocationTimeLabels(props.card))

/** Motivo de selo sem documento chega sem status: conta como Pendente (A4 §5). */
const statusOf = (item: EligibilityReason): ProfessionalDocumentStatus =>
  item.status ?? ProfessionalDocumentStatus.PENDING

const pendingDetail = (item: EligibilityReason): string =>
  pendingReasonText({
    status: statusOf(item),
    pendingReason: item.pendingReason,
    expiresAt: item.expiresAt
  })

function onDragStart(event: DragEvent) {
  // Firefox só inicia o arraste com algum dado; quem lê o cartão é o Kanban (evento abaixo).
  event.dataTransfer?.setData('text/plain', String(props.card.id))
  emit('dragStart', props.card)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(["flex flex-col gap-2 rounded-lg border bg-white p-2.5 text-left shadow-sm", [
      critical.value ? 'border-red-300' : 'border-[#e8e8ed]',
      canDrag.value ? 'cursor-grab active:cursor-grabbing' : ''
    ]]),
    draggable: canDrag.value,
    "aria-busy": _ctx.isMoving ? 'true' : 'false',
    "data-testid": `pre-allocation-card-${_ctx.card.id}`,
    onDragstart: onDragStart
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.card.professional.name), 1),
        _createElementVNode("p", _hoisted_5, [
          (_ctx.card.professional.crm)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode("CRM " + _toDisplayString(_ctx.card.professional.crm) + " · ", 1)
              ], 64))
            : _createCommentVNode("", true),
          _createTextVNode(_toDisplayString(_ctx.card.company.name), 1)
        ])
      ]),
      (timer.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums", TIMER_CLASS[timer.value.tone]]),
            title: 
          critical.value ? 'Menos de 24h para o plantão mais próximo' : 'Tempo até o plantão mais próximo'
        ,
            "data-testid": `pre-allocation-card-${_ctx.card.id}-countdown`
          }, _toDisplayString(timer.value.label), 11, _hoisted_6))
        : (_openBlock(), _createBlock(LabelBadge, {
            key: 1,
            label: "Definitiva",
            color: "green",
            size: "small",
            icon: _unref(mdiCheckCircle)
          }, null, 8, ["icon"]))
    ]),
    _createElementVNode("ul", _hoisted_7, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stations.value, (station) => {
        return (_openBlock(), _createElementBlock("li", {
          key: station.doctorStationId,
          class: "flex items-center gap-1 text-[11px] text-textPrimary"
        }, [
          (_openBlock(), _createElementBlock("svg", _hoisted_8, [
            _createElementVNode("path", {
              d: _unref(mdiCalendarClock),
              fill: "currentColor"
            }, null, 8, _hoisted_9)
          ])),
          _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(formatStationStart)(station.start)) + " · " + _toDisplayString(station.scheduleName), 1)
        ]))
      }), 128))
    ]),
    (_ctx.card.missing.length)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.card.missing, (item) => {
            return (_openBlock(), _createElementBlock("li", {
              key: `${item.field}-${item.key}`
            }, [
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("span", _hoisted_13, _toDisplayString(item.name), 1),
                _createVNode(LabelBadge, {
                  label: _unref(DOCUMENT_STATUS_LABEL)[statusOf(item)],
                  color: statusOf(item) === _unref(ProfessionalDocumentStatus).IN_REVIEW ? 'yellow' : 'red',
                  size: "small",
                  icon: null
                }, null, 8, ["label", "color"])
              ]),
              (pendingDetail(item))
                ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(pendingDetail(item)), 1))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("dl", _hoisted_15, [
      _hoisted_16,
      _createElementVNode("dd", _hoisted_17, _toDisplayString(times.value.completion), 1),
      _hoisted_18,
      _createElementVNode("dd", _hoisted_19, _toDisplayString(times.value.decision), 1),
      _hoisted_20,
      _createElementVNode("dd", {
        class: _normalizeClass(["text-right font-semibold tabular-nums", resolved.value ? 'text-sucess' : 'text-textPrimary'])
      }, _toDisplayString(times.value.waiting), 3)
    ]),
    (_ctx.canMove && !resolved.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          type: "button",
          class: "self-start text-[11px] font-medium text-primary30 underline disabled:opacity-60",
          disabled: _ctx.isMoving,
          "data-testid": `pre-allocation-card-${_ctx.card.id}-resolve`,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('tryResolve', _ctx.card)))
        }, " Marcar como resolvido ", 8, _hoisted_21))
      : _createCommentVNode("", true)
  ], 42, _hoisted_1))
}
}

})